import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import { TrendingUp } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { isSaudiBrand } from '../../../products/saudi/utils/isSaudiBrand';
import AnalyticsChartCard from '../analytics/AnalyticsChartCard';
import DashboardLayout from './DashboardLayout';
import KPIGrid from './KPIGrid';
import QuickAction from './QuickAction';
import useModuleDashboard from './useModuleDashboard';

const QAWAEM_BAR_GRADIENTS = [
  'linear-gradient(90deg, #3a624e 0%, #6e9b6e 100%)',
  'linear-gradient(90deg, #a67c3d 0%, #c4963c 100%)',
  'linear-gradient(90deg, #5a855a 0%, #8fb08f 100%)',
  'linear-gradient(90deg, #8a624c 0%, #b8926a 100%)',
  'linear-gradient(90deg, #2e452e 0%, #5a855a 100%)',
  'linear-gradient(90deg, #7a6348 0%, #a67c3d 100%)',
];

function ChartPanel({ title, rows = [], emptyLabel, qawaem = false }) {
  const max = Math.max(...rows.map((r) => Number(r.value) || 0), 1);
  const total = rows.reduce((sum, r) => sum + (Number(r.value) || 0), 0);

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.25 }}
      className={qawaem ? 'qawaem-dash-panel glass-chart-green' : 'glass-panel-strong p-6'}
    >
      <div className={qawaem ? 'qawaem-dash-panel-head' : 'mb-4'}>
        <div>
          <h3 className={qawaem ? 'qawaem-dash-panel-title' : 'text-lg font-semibold text-white'}>{title}</h3>
          {qawaem && total > 0 ? <p className="qawaem-dash-panel-sub">{total} إجمالي</p> : null}
        </div>
      </div>
      {rows.length ? (
        <div className="space-y-3">
          {rows.map((row, index) => {
            const label = row?.label != null ? String(row.label) : '';
            const value = Number(row.value) || 0;
            const pct = Math.max(8, (value / max) * 100);
            const share = total > 0 ? Math.round((value / total) * 100) : 0;
            return (
              <div key={`${label}-${index}`}>
                <div
                  className={`flex justify-between text-sm mb-1 gap-2 ${
                    qawaem ? 'text-console' : 'text-white/70'
                  }`}
                >
                  <span className={`truncate ${qawaem ? 'font-medium' : ''}`}>{label}</span>
                  <div className="flex items-center gap-2 shrink-0 tabular-nums">
                    {qawaem ? <span className="text-xs text-console-muted">{share}%</span> : null}
                    <span className={qawaem ? 'font-semibold text-forest-900' : ''}>{row.value}</span>
                  </div>
                </div>
                <div className={qawaem ? 'qawaem-dash-bar-track' : 'h-2 rounded-full bg-white/10 overflow-hidden'}>
                  <div
                    className={qawaem ? 'qawaem-dash-bar-fill' : 'h-full rounded-full bg-gradient-to-r from-emerald-500 to-amber-500'}
                    style={{
                      width: `${pct}%`,
                      ...(qawaem
                        ? { background: QAWAEM_BAR_GRADIENTS[index % QAWAEM_BAR_GRADIENTS.length] }
                        : {}),
                    }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <p className={`text-sm ${qawaem ? 'text-console-muted' : 'text-white/50'}`}>{emptyLabel}</p>
      )}
    </motion.div>
  );
}

function RecentList({ title, items = [], emptyLabel, qawaem = false }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.3 }}
      className={qawaem ? 'qawaem-dash-panel' : 'glass-panel p-6'}
    >
      <div className={qawaem ? 'qawaem-dash-panel-head' : 'mb-4'}>
        <div>
          <h3 className={qawaem ? 'qawaem-dash-panel-title' : 'text-lg font-semibold text-white'}>{title}</h3>
          {qawaem && items.length ? <p className="qawaem-dash-panel-sub">{items.length} عنصر</p> : null}
        </div>
      </div>
      {items.length ? (
        <ul className={qawaem ? 'divide-y divide-[#6e9b6e]/10' : 'space-y-3'}>
          {items.map((item, index) => (
            <li
              key={item.id || item.label}
              className={
                qawaem
                  ? `flex items-center justify-between gap-3 py-3 first:pt-0 last:pb-0 ${
                      index === 0 ? 'qawaem-dash-recent-featured' : ''
                    }`
                  : 'flex items-start justify-between gap-3 border-b border-white/10 pb-3 last:border-0 last:pb-0'
              }
            >
              <div className="min-w-0">
                <p
                  className={`text-sm font-medium truncate ${
                    qawaem ? 'text-forest-900 font-semibold' : 'text-white/90'
                  }`}
                >
                  {item.label}
                </p>
                {item.meta ? (
                  <p className={`text-xs mt-0.5 truncate ${qawaem ? 'text-console-muted' : 'text-white/50'}`}>
                    {item.meta}
                  </p>
                ) : null}
              </div>
              {item.date ? (
                <span
                  className={`text-xs shrink-0 tabular-nums ${
                    qawaem ? 'text-console-muted' : 'text-white/40'
                  }`}
                >
                  {item.date}
                </span>
              ) : null}
            </li>
          ))}
        </ul>
      ) : (
        <p className={`text-sm ${qawaem ? 'text-console-muted' : 'text-white/50'}`}>{emptyLabel}</p>
      )}
    </motion.div>
  );
}

export default function ModernModuleDashboard({
  apiEndpoint,
  apiQueryParams = null,
  title,
  subtitle,
  icon,
  accent,
  heroKpi,
  kpiItems = [],
  quickLinks = [],
  chartTitle,
  catalogBreakdownTitle,
  recentTitle,
  mapData,
  refreshEvents = [],
}) {
  const { t } = useTranslation();
  const qawaem = isSaudiBrand();
  const { data, loading, error, refresh } = useModuleDashboard(apiEndpoint, apiQueryParams, refreshEvents);
  const emptyLabel = t('common.no_data', { defaultValue: 'لا توجد بيانات' });

  const mapped = useMemo(() => {
    if (!data || !mapData) {
      return {
        hero: heroKpi,
        kpis: kpiItems,
        charts: [],
        chartRows: [],
        recentItems: [],
      };
    }
    return mapData(data, { t, emptyLabel });
  }, [data, mapData, heroKpi, kpiItems, t, emptyLabel]);

  const resolvedCatalogTitle =
    catalogBreakdownTitle ||
    mapped.catalogBreakdownTitle ||
    t('common.overview', { defaultValue: 'نظرة عامة' });
  const resolvedChartTitle = chartTitle || t('common.overview', { defaultValue: 'نظرة عامة' });
  const resolvedRecentTitle = recentTitle || t('common.recent_activity', { defaultValue: 'النشاط الأخير' });

  const charts = mapped.charts || [];
  const useAnalyticsCharts = qawaem && charts.length > 0;
  const headCharts = useAnalyticsCharts ? charts.slice(0, 2) : [];
  const tailCharts = useAnalyticsCharts ? charts.slice(2) : [];

  return (
    <DashboardLayout
      title={title}
      subtitle={subtitle}
      icon={icon}
      accent={accent}
      loading={loading}
      error={error}
      onRefresh={refresh}
    >
      {!loading ? (
        <div className="space-y-4">
          <KPIGrid hero={mapped.hero} items={mapped.kpis} />

          {useAnalyticsCharts ? (
            <>
              {headCharts.length ? (
                <div className={`grid gap-4 ${headCharts.length > 1 ? 'grid-cols-1 lg:grid-cols-2' : 'grid-cols-1'}`}>
                  {headCharts.map((chart) => (
                    <AnalyticsChartCard key={chart.title} chart={chart} empty={emptyLabel} qawaem />
                  ))}
                </div>
              ) : null}

              <div className={`grid gap-4 ${tailCharts.length ? 'grid-cols-1 lg:grid-cols-2' : 'grid-cols-1'}`}>
                {tailCharts.map((chart) => (
                  <AnalyticsChartCard key={chart.title} chart={chart} empty={emptyLabel} qawaem />
                ))}
                <RecentList
                  title={resolvedRecentTitle}
                  items={mapped.recentItems}
                  emptyLabel={emptyLabel}
                  qawaem
                />
              </div>
            </>
          ) : (
            <>
              {mapped.catalogBreakdown?.length ? (
                <ChartPanel
                  title={resolvedCatalogTitle}
                  rows={mapped.catalogBreakdown}
                  emptyLabel={emptyLabel}
                  qawaem={qawaem}
                />
              ) : null}

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <ChartPanel
                  title={resolvedChartTitle}
                  rows={mapped.chartRows}
                  emptyLabel={emptyLabel}
                  qawaem={qawaem}
                />
                <RecentList
                  title={resolvedRecentTitle}
                  items={mapped.recentItems}
                  emptyLabel={emptyLabel}
                  qawaem={qawaem}
                />
              </div>
            </>
          )}

          {quickLinks.length ? (
            <div className={qawaem ? 'qawaem-dash-quick-section' : undefined}>
              <div className={qawaem ? 'qawaem-dash-quick-head' : 'flex items-center gap-2 mb-3'}>
                {qawaem ? <TrendingUp className="h-4 w-4 text-[#6e9b6e]" aria-hidden /> : null}
                <h3 className={`text-lg font-semibold ${qawaem ? 'qawaem-dash-panel-title' : 'text-white'}`}>
                  {t('common.quick_access', { defaultValue: 'وصول سريع' })}
                </h3>
              </div>
              <div className={qawaem ? 'qawaem-dash-quick-grid' : 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3'}>
                {quickLinks.map((link, i) => (
                  <QuickAction key={link.href} {...link} index={i} />
                ))}
              </div>
            </div>
          ) : null}
        </div>
      ) : null}
    </DashboardLayout>
  );
}
